import { Columns3Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface OptionalColumn<Key extends string> {
  readonly key: Key;
  readonly label: string;
}

interface ColumnsMenuProps<Key extends string> {
  readonly columns: readonly OptionalColumn<Key>[];
  readonly active: readonly Key[];
  readonly onToggle: (key: Key) => void;
}

/**
 * The optional-columns dropdown a wide table opens with.
 *
 * Which columns are on lives in the URL, like every other filter on these
 * screens, so a colleague opening a pasted link sees the table the sender was
 * looking at rather than the default one.
 */
export function ColumnsMenu<Key extends string>({ columns, active, onToggle }: ColumnsMenuProps<Key>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn(active.length > 0 && 'border-primary/40')}>
          <Columns3Icon aria-hidden="true" className="text-muted-foreground" />
          Columns
          {active.length > 0 ? (
            <span className="font-mono text-xs text-muted-foreground">+{active.length}</span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[232px] p-1.5">
        <p className="px-2 pt-1 pb-1.5 text-xs font-semibold tracking-[0.055em] text-muted-foreground uppercase">
          Optional columns
        </p>
        {columns.map((column) => {
          const id = `column-${column.key}`;

          return (
            <Label
              key={column.key}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] font-normal hover:bg-accent"
            >
              <Checkbox
                id={id}
                checked={active.includes(column.key)}
                onCheckedChange={() => {
                  onToggle(column.key);
                }}
              />
              {column.label}
            </Label>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
